/* eslint-disable max-lines-per-function */
import { useEffect, useRef, useState } from "react";
import { cn } from "tailwind-variants";
import { useI18n } from "intl/useI18n";
import { api } from "jiffy-api";
import { REDRAW_NOTES_MAX_LENGTH } from "common/redraw-constants";
import { REDRAW_NOTES_TEXT_AREA } from "../../common/dtfUserRetentionHelper";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import { useDtfStore } from "../../store";
import "./CustomNotes.scss";

export default function CustomNotes({
  disabled,
  onLearnMore,
  index,
  modelValue,
  onInput,
  onUpdateModelValue,
  ...restProps
}) {
  const { n: i18nN } = useI18n();
  const store = useDtfStore();

  const [model, setModel] = useState(modelValue ?? "");
  const [isSavingVisible, setIsSavingVisible] = useState(false);
  const [isSavedVisible, setIsSavedVisible] = useState(false);
  const textareaRef = useRef(null);
  const savingTimeoutIdRef = useRef(null);

  function isStandardDTFProduct() {
    return store.isStandardDTFProduct;
  }

  function placeholderText() {
    if (isStandardDTFProduct()) {
      return "i.e. Make the font black, remove the red background, and smooth out edges.";
    }
    return 'Example: "Change the color of the font to black. Remove the background from the top half of the image."';
  }

  function artwork() {
    if (index === undefined) return null;
    return store.artworks[index];
  }

  function shouldShowPrice() {
    if (!artwork()?.pricing) return false;
    const price = artwork().pricing.expertEditAddonTierPrice;
    return Number(price) > 0;
  }

  function formattedPrice() {
    if (!artwork()?.pricing) return "";
    const price = artwork().pricing.expertEditAddonTierPrice;
    return i18nN(price, "currency");
  }

  function autoResize(event) {
    const textarea = event.target;
    if (textarea && isStandardDTFProduct()) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }

  function handleSavingIndicator() {
    setIsSavingVisible(true);
    setIsSavedVisible(false);
    if (savingTimeoutIdRef.current !== null) {
      clearTimeout(savingTimeoutIdRef.current);
    }
    savingTimeoutIdRef.current = window.setTimeout(() => {
      saveRedrawNote();
    }, 1000);
  }

  function saveRedrawNote() {
    if (!artwork()?.id) return;
    const note = model.trim();
    api.dtf
      .updateArtworkRedrawData(
        {
          id: artwork().id
        },
        {
          dtf_artwork: {
            enhancement_request_note: note
          }
        }
      )
      .then(() => {
        setIsSavingVisible(false);
        setIsSavedVisible(true);
      })
      .catch(() => {
        setIsSavingVisible(false);
        setIsSavedVisible(false);
      });
  }

  function handleTextareaChange(e) {
    const { value } = e.target;
    setModel(value);
    if (onUpdateModelValue) {
      onUpdateModelValue(value);
    }
    autoResize(e);
    if (onInput) {
      onInput(e);
    }
    handleSavingIndicator();
  }

  useEffect(() => {
    setModel(modelValue ?? "");
  }, [modelValue]);

  useEffect(() => {
    if (isStandardDTFProduct() && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [model]);

  useEffect(
    () => () => {
      if (savingTimeoutIdRef.current !== null) {
        clearTimeout(savingTimeoutIdRef.current);
      }
    },
    []
  );

  return (
    <div {...restProps} className={cn("custom-notes scope-db2f53a3", restProps.className)}>
      <label className="custom-notes__label scope-db2f53a3" data-testid="custom-notes-label">
        <span className="custom-notes__label-text scope-db2f53a3">
          {isStandardDTFProduct() ? (
            <span className="custom-notes__label-with-badge scope-db2f53a3">
              <span className="custom-notes__label-text-with-icon scope-db2f53a3">
                {" "}
                Need artist edits?{" "}
                {onLearnMore ? (
                  <button
                    className="custom-notes__learn-more-icon scope-db2f53a3"
                    type="button"
                    onClick={onLearnMore}
                    data-testid="custom-notes-learn-more-icon"
                  >
                    <UseSvg
                      className="custom-notes__question-icon scope-db2f53a3"
                      icon="question-circle"
                      data-testid="custom-notes-question-icon"
                    />
                  </button>
                ) : null}
              </span>
              {isSavingVisible ? (
                <span className="custom-notes__saving-status scope-db2f53a3">
                  <UseSvg
                    className="custom-notes__saving-icon scope-db2f53a3"
                    icon="rotate"
                    data-testid="custom-notes-saving-icon"
                  />{" "}
                  Saving...{" "}
                </span>
              ) : isSavedVisible ? (
                <span className="custom-notes__saving-status custom-notes__saving-status--saved scope-db2f53a3">
                  <UseSvg
                    className="custom-notes__saved-icon scope-db2f53a3"
                    icon="check"
                    data-testid="custom-notes-saved-icon"
                  />{" "}
                  Saved{" "}
                </span>
              ) : null}
              {!shouldShowPrice() ? (
                <span className="custom-notes__free-badge scope-db2f53a3">Free</span>
              ) : (
                <span className="custom-notes__price-badge scope-db2f53a3">{formattedPrice()}</span>
              )}
            </span>
          ) : (
            <>
              <span className="custom-notes__label-required scope-db2f53a3">*</span>
              <span className="scope-db2f53a3">Instructions for our designers</span>
              {isSavingVisible ? (
                <span className="custom-notes__saving-status scope-db2f53a3">
                  <UseSvg
                    className="custom-notes__saving-icon scope-db2f53a3"
                    icon="rotate"
                    data-testid="custom-notes-saving-icon-1"
                  />{" "}
                  Saving...{" "}
                </span>
              ) : isSavedVisible ? (
                <span className="custom-notes__saving-status custom-notes__saving-status--saved scope-db2f53a3">
                  <UseSvg
                    className="custom-notes__saved-icon scope-db2f53a3"
                    icon="check"
                    data-testid="custom-notes-saved-icon-1"
                  />{" "}
                  Saved{" "}
                </span>
              ) : null}
            </>
          )}
        </span>
        {isStandardDTFProduct() ? (
          <div className="custom-notes__textarea-wrapper scope-db2f53a3">
            <textarea
              className="custom-notes__textarea custom-notes__textarea--experiment js-custom-notes-textarea scope-db2f53a3"
              ref={textareaRef}
              value={model}
              onChange={handleTextareaChange}
              data-redraw-text-area-identifier={REDRAW_NOTES_TEXT_AREA}
              disabled={disabled}
              maxLength={REDRAW_NOTES_MAX_LENGTH}
              placeholder={placeholderText()}
              data-testid="custom-notes-textarea"
            />
          </div>
        ) : (
          <textarea
            className="custom-notes__textarea js-custom-notes-textarea scope-db2f53a3"
            value={model}
            onChange={handleTextareaChange}
            data-redraw-text-area-identifier={REDRAW_NOTES_TEXT_AREA}
            disabled={disabled}
            maxLength={REDRAW_NOTES_MAX_LENGTH}
            placeholder={placeholderText()}
            data-testid="custom-notes-textarea-1"
          />
        )}
      </label>
    </div>
  );
}
